import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../../core/service/api-service/api-service';
import { API } from '../../../../core/API';
import { PermissionService } from '../../../../core/service/permission/permission.service';

interface PublicDocument {
  public_document_id: number;
  public_document_first_title: string;
  page_code: string;
  public_document_display_order: number;
  public_document_status?: 'ACTI' | 'INAC';
  public_document_status_label?: string;
  file_url: string | null;
  file_name?: string;
  file_extension?: string;
}

@Component({
  selector: 'app-documents-public-detail',
  standalone: false,
  templateUrl: './documents-public-detail.component.html',
  styleUrls: ['./documents-public-detail.component.scss'],
})
export class DocumentsPublicDetailComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);
  private sanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();

  readonly pageCategories = [
    { code: 'STATUT_FRMTT', label: 'Statuts FRMTT' },
    { code: 'REGLEMENTS', label: 'Règlements' },
    { code: 'AG_RAPPORTS', label: 'Rapports AG' },
    { code: 'LOI_30_09', label: 'Loi 30-09' },
  ];

  document = signal<PublicDocument | null>(null);
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);

  pendingStatus = signal<{ status: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal(false);
  isSaving = signal(false);

  safePdfUrl = computed((): SafeResourceUrl | null => {
    const fileUrl = this.document()?.file_url;
    if (!fileUrl) return null;
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
  });

  fileExt = computed((): string => {
    const doc = this.document();
    if (!doc) return '';
    if (doc.file_extension) return doc.file_extension.toLowerCase();
    const name = doc.file_name || '';
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  });

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.api.get(`${API.DOCUMENTS.GET_BY_ID}/${id}`).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => { this.document.set(res.data || res); this.isLoading.set(false); },
      error: () => { this.errorMessage.set('Impossible de charger ce document'); this.isLoading.set(false); },
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  goBack(): void { this.router.navigate(['/federation/documents-publics']); }
  onEdit(): void { this.router.navigate(['/federation/documents-publics', this.document()!.public_document_id, 'edit']); }

  confirmStatus(status: 'ACTI' | 'INAC'): void { this.pendingStatus.set({ status }); }
  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const doc = this.document();
    const pending = this.pendingStatus();
    if (!doc || !pending) return;
    this.isSaving.set(true);
    this.pendingStatus.set(null);
    this.api.put(`${API.DOCUMENTS.UPDATE}/${doc.public_document_id}`, { public_document_status: pending.status })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.document.update(prev => prev ? { ...prev, public_document_status: pending.status } : prev);
          this.isSaving.set(false);
        },
        error: () => { this.errorMessage.set('Erreur lors du changement de statut'); this.isSaving.set(false); },
      });
  }

  confirmDelete(): void { this.pendingDelete.set(true); }
  cancelDelete(): void { this.pendingDelete.set(false); }

  applyDelete(): void {
    const doc = this.document();
    if (!doc) return;
    this.isSaving.set(true);
    this.pendingDelete.set(false);
    this.api.delete(`${API.DOCUMENTS.DELETE}/${doc.public_document_id}`).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.router.navigate(['/federation/documents-publics']),
      error: () => { this.errorMessage.set('Erreur lors de la suppression'); this.isSaving.set(false); },
    });
  }

  getCategoryLabel(code: string): string {
    return this.pageCategories.find(c => c.code === code)?.label ?? code;
  }

  statusLabel(status: string): string {
    return ({ ACTI: 'En ligne', INAC: 'Hors ligne' } as any)[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  pendingStatusLabel(status: 'ACTI' | 'INAC'): string {
    return ({ ACTI: 'Publier', INAC: 'Dépublier' } as any)[status];
  }
}
